import {
  REAL_ESTATE_CAPABILITY_MATRIX,
  REAL_ESTATE_CAPABILITY_STATES,
} from 'dashboard/constants/permissions';

export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

export const getCurrentAccount = ({ accounts = [] } = {}, accountId = null) => {
  return accounts.find(account => Number(account.id) === Number(accountId));
};

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.permissions || [];
};

export const getUserRole = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (currentAccount.custom_role_id) {
    return 'custom_role';
  }

  return currentAccount.role || 'agent';
};

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};

const toArray = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const unique = values => [...new Set(toArray(values))];

const truthyValue = value =>
  value === true ||
  value === 'true' ||
  value === '1' ||
  value === 1 ||
  value === 'partial' ||
  value === 'stale' ||
  value === 'permission_denied' ||
  value === 'permission-denied';

const pickFirstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const readCapabilityValue = (capabilities, key) => {
  if (!capabilities || !key) return undefined;

  if (Array.isArray(capabilities)) {
    return capabilities.includes(key) ? true : undefined;
  }

  const direct = capabilities[key];
  if (direct !== undefined) return direct;

  const nested = key.split('.').reduce((result, part) => {
    if (result === undefined || result === null) return undefined;
    return result[part];
  }, capabilities);
  return nested;
};

const isExplicitlyDenied = value =>
  value === false ||
  value === 'false' ||
  value === 'denied' ||
  value === 'permission_denied' ||
  value === 'permission-denied' ||
  value === 'disabled';

const isExplicitlyAllowed = value =>
  value === true ||
  value === 'true' ||
  value === 'allowed' ||
  value === 'ready';

const includesStatus = (collection, statuses) => {
  if (!Array.isArray(collection)) return false;
  return collection.some(item => {
    const status = String(
      item?.status || item?.state || item?.availability || ''
    ).toLowerCase();
    return statuses.includes(status);
  });
};

const realEstateStateStatus = ({ permissionDenied, stale, partial }) => {
  if (permissionDenied) return REAL_ESTATE_CAPABILITY_STATES.PERMISSION_DENIED;
  if (stale) return REAL_ESTATE_CAPABILITY_STATES.STALE;
  if (partial) return REAL_ESTATE_CAPABILITY_STATES.PARTIAL;
  return REAL_ESTATE_CAPABILITY_STATES.READY;
};

export const getRealEstateCapabilityState = ({
  meta = {},
  error = null,
} = {}) => {
  const status = error?.response?.status || error?.status;
  const authorizationStatus = String(
    pickFirstPresent(
      meta.authorization_status,
      meta.authorizationStatus,
      meta.permission_status,
      meta.permissionStatus,
      meta.access_status,
      meta.accessStatus
    ) || ''
  ).toLowerCase();
  const sourceStates = meta.sources || meta.source_states || meta.sourceStates;

  const permissionDenied =
    status === 403 ||
    truthyValue(meta.permission_denied) ||
    truthyValue(meta.permissionDenied) ||
    authorizationStatus === 'denied' ||
    authorizationStatus === 'permission_denied';
  const partial =
    !permissionDenied &&
    (truthyValue(meta.partial) ||
      truthyValue(meta.partial_results) ||
      truthyValue(meta.partialResults) ||
      truthyValue(meta.is_partial) ||
      truthyValue(meta.isPartial) ||
      includesStatus(sourceStates, ['partial', 'degraded']));
  const stale =
    !permissionDenied &&
    (truthyValue(meta.stale) ||
      truthyValue(meta.is_stale) ||
      truthyValue(meta.isStale) ||
      Boolean(meta.stale_at || meta.staleAt) ||
      includesStatus(sourceStates, ['stale', 'expired']));

  return {
    permissionDenied,
    partial,
    stale,
    status: realEstateStateStatus({ permissionDenied, stale, partial }),
  };
};

export const getRealEstateCapabilitiesFromMeta = (meta = {}) =>
  pickFirstPresent(
    meta.capabilities,
    meta.real_estate_capabilities,
    meta.realEstateCapabilities,
    meta.permissions_matrix,
    meta.permissionsMatrix
  ) || {};

const getAccountCapabilityPayload = account =>
  pickFirstPresent(
    account?.capabilities,
    account?.real_estate_capabilities,
    account?.realEstateCapabilities
  ) || {};

const hasCapabilityPayload = value => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(value);
};

const disabledReasonFor = ({
  deniedByPermission,
  deniedByCapability,
  blockedByStale,
  blockedByPartial,
}) => {
  if (deniedByPermission) {
    return REAL_ESTATE_CAPABILITY_STATES.PERMISSION_DENIED;
  }
  if (deniedByCapability) return 'capability_denied';
  if (blockedByStale) return REAL_ESTATE_CAPABILITY_STATES.STALE;
  if (blockedByPartial) return REAL_ESTATE_CAPABILITY_STATES.PARTIAL;
  return '';
};

export const getPermissionTokensForAccount = account => {
  const permissionTokens = [
    ...toArray(account?.permissions),
    account?.role,
    account?.role_name,
    account?.roleName,
  ];

  if (account?.custom_role_id) {
    permissionTokens.push('custom_role');
  }

  return unique(permissionTokens);
};

export const evaluateRealEstateCapability = ({
  surface,
  action,
  permissions = [],
  capabilities = {},
  state = getRealEstateCapabilityState(),
  matrix = REAL_ESTATE_CAPABILITY_MATRIX,
} = {}) => {
  const surfaceMatrix = matrix[surface] || {};
  const entry = surfaceMatrix[action] || {};
  const requiredPermissions = toArray(entry.permissions);
  const capabilityIds = toArray(entry.capabilityIds || entry.capabilities);
  const permissionAllowed =
    requiredPermissions.length === 0 ||
    hasPermissions(requiredPermissions, permissions);

  const capabilityChecks = capabilityIds
    .map(id => readCapabilityValue(capabilities, id))
    .filter(value => value !== undefined);
  const capabilityAllowed =
    capabilityChecks.length === 0 ||
    capabilityChecks.some(value => isExplicitlyAllowed(value)) ||
    capabilityChecks.every(value => !isExplicitlyDenied(value));

  const deniedByPermission = state.permissionDenied || !permissionAllowed;
  const deniedByCapability = !capabilityAllowed;
  const blockedByPartial = Boolean(entry.disableWhenPartial && state.partial);
  const blockedByStale = Boolean(entry.disableWhenStale && state.stale);
  const visible = !deniedByPermission || entry.deniedStrategy === 'disable';
  const disabled =
    deniedByPermission ||
    deniedByCapability ||
    blockedByPartial ||
    blockedByStale;
  const disabledReason = disabledReasonFor({
    deniedByPermission,
    deniedByCapability,
    blockedByStale,
    blockedByPartial,
  });

  return {
    surface,
    action,
    capabilityIds,
    permissions: requiredPermissions,
    visible,
    disabled,
    allowed: visible && !disabled,
    deniedBy: disabledReason,
    state: state.status,
  };
};

export const buildRealEstateCapabilityContext = ({
  surface,
  user,
  accountId,
  account,
  permissions,
  capabilities,
  meta = {},
  error = null,
  matrix = REAL_ESTATE_CAPABILITY_MATRIX,
} = {}) => {
  const currentAccount = account || getCurrentAccount(user, accountId) || {};
  const permissionTokens = permissions
    ? unique(permissions)
    : getPermissionTokensForAccount(currentAccount);
  const metaCapabilities = getRealEstateCapabilitiesFromMeta(meta);
  const accountCapabilities = getAccountCapabilityPayload(currentAccount);
  const capabilityPayload =
    capabilities ||
    (hasCapabilityPayload(metaCapabilities)
      ? metaCapabilities
      : accountCapabilities);
  const baseState = getRealEstateCapabilityState({ meta, error });
  const surfaceMatrix = matrix[surface] || {};
  const actions = Object.keys(surfaceMatrix).reduce((result, action) => {
    result[action] = evaluateRealEstateCapability({
      surface,
      action,
      permissions: permissionTokens,
      capabilities: capabilityPayload,
      state: baseState,
      matrix,
    });
    return result;
  }, {});
  const pageDenied =
    baseState.permissionDenied || actions.view?.visible === false;
  const state = {
    ...baseState,
    permissionDenied: pageDenied,
    status: pageDenied
      ? REAL_ESTATE_CAPABILITY_STATES.PERMISSION_DENIED
      : baseState.status,
  };

  return {
    surface,
    account: currentAccount,
    permissions: permissionTokens,
    capabilities: capabilityPayload,
    state,
    actions,
  };
};

export const buildRealEstateCapabilityAuditMatrix = ({
  surface,
  context = {},
  matrix = REAL_ESTATE_CAPABILITY_MATRIX,
} = {}) => {
  const surfaceMatrix = matrix[surface] || {};
  const state = context.state || getRealEstateCapabilityState();

  return Object.entries(surfaceMatrix).map(([action, entry]) => {
    const evaluated =
      context.actions?.[action] ||
      evaluateRealEstateCapability({
        surface,
        action,
        permissions: context.permissions || [],
        capabilities: context.capabilities || {},
        state,
        matrix,
      });

    return {
      surface,
      action,
      capabilityIds: toArray(entry.capabilityIds || entry.capabilities),
      permissions: toArray(entry.permissions),
      blocksPartial: Boolean(entry.disableWhenPartial),
      blocksStale: Boolean(entry.disableWhenStale),
      visible: evaluated.visible,
      disabled: evaluated.disabled,
      allowed: evaluated.allowed,
      deniedBy: evaluated.deniedBy,
      state: evaluated.state,
    };
  });
};
